import React, { useState } from 'react';
import { blogCandidatesService } from '../../../../services/blogCandidatesService';
import { blogRunsService } from '../../../../services/blogRunsService';
import type { WordpressBlogCategory } from '../../../../services/blogWordpressCategoriesService';
import type { ArticleRow } from './articleTypes';

interface ArticleListProps {
  articles: ArticleRow[];
  articlesLoading: boolean;
  showPublishedArticles: boolean;
  setShowPublishedArticles: (v: boolean) => void;
  wordpressCategories: WordpressBlogCategory[];
  onSelectArticle: (candidateId: string) => void;
  onNewArticleCreated: (candidateId: string) => void;
  onArticlesError: (msg: string) => void;
  loadArticles: () => Promise<void>;
}

export const ArticleList: React.FC<ArticleListProps> = ({
  articles,
  articlesLoading,
  showPublishedArticles,
  setShowPublishedArticles,
  wordpressCategories,
  onSelectArticle,
  onNewArticleCreated,
  onArticlesError,
  loadArticles,
}) => {
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      {articlesLoading && (
        <div className="flex flex-col items-center justify-center h-32 mb-6">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-talaera-blue"></div>
          <p className="mt-4 text-gray-600">Loading articles...</p>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-4">
          <h3 className="text-sm font-medium text-gray-700">Articles in progress</h3>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={showPublishedArticles}
              onChange={(e) => setShowPublishedArticles(e.target.checked)}
            />
            Show published articles
          </label>
        </div>
        <button
          disabled={creating}
          onClick={async () => {
            setCreating(true);
            try {
              const created = await blogCandidatesService.create({
                candidate_name: 'New Article',
                icp: 'To be defined',
                funnel_stage: 'Top',
                focus: 'B2B',
                wordpress_category_slug: wordpressCategories[0]?.slug || '',
                status: 'draft',
              });
              await blogRunsService.create(created.candidate_id, { prompt_instances: {} });
              await loadArticles();
              onNewArticleCreated(created.candidate_id);
            } catch (e: any) {
              onArticlesError(e?.message || 'Failed to create article');
            } finally {
              setCreating(false);
            }
          }}
          className="px-3 py-1 text-white text-sm rounded hover:opacity-90 shrink-0 disabled:opacity-50"
          style={{ backgroundColor: '#1a2332' }}
        >
          {creating ? 'Creating...' : 'New Article'}
        </button>
      </div>

      {/* Articles table */}
      <div className="bg-white border rounded p-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #1a2332 0%, #2d3748 100%)' }}>
              <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm">Article</th>
              <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm w-20">Stage</th>
              <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm">Updated</th>
              <th className="px-3 py-2.5 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {articles.map(row => {
              const stageId = row.currentStageId || '';
              const stageNumber = stageId ? stageId.replace('stage_', '').split('_')[0] : '\u2014';
              return (
                <tr key={row.candidate.candidate_id} className="cursor-pointer hover:bg-gray-50 transition-all" style={{ boxShadow: 'inset 0 0 0 0 #01bcd4' }} onMouseEnter={(e) => (e.currentTarget.style.boxShadow = 'inset 4px 0 0 0 #01bcd4')} onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'inset 0 0 0 0 #01bcd4')} onClick={() => { onSelectArticle(row.candidate.candidate_id); }}>
                  <td className="px-3 py-3">
                    <div className="font-medium text-gray-900 text-[15px]">{row.candidate.candidate_name}</div>
                    {row.candidate.wordpress_category?.name && <div className="text-xs text-gray-400 mt-0.5">{row.candidate.wordpress_category.name}</div>}
                  </td>
                  <td className="px-3 py-3 text-gray-500 font-mono text-sm">{stageNumber}</td>
                  <td className="px-3 py-3 text-gray-500 text-sm">{row.run?.updatedAt ? new Date(row.run.updatedAt).toLocaleDateString() : new Date(row.candidate.updatedAt).toLocaleDateString()}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete article"
                      onClick={(e) => { e.stopPropagation(); setArchiveConfirmId(row.candidate.candidate_id); }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" />
                      </svg>
                    </button>
                  </td>
                </tr>
              );
            })}
            {articles.length === 0 && !articlesLoading && (
              <tr><td className="px-3 py-4 text-gray-500" colSpan={4}>{showPublishedArticles ? 'No articles yet.' : 'No in-progress articles yet. Check "Show published articles" to include completed posts.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Archive confirmation dialog */}
      {archiveConfirmId && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal="true" onClick={() => setArchiveConfirmId(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5 space-y-4 border border-gray-200" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Delete article?</h3>
            <p className="text-sm text-gray-600">This will archive the article and remove it from the pipeline. The data will not be permanently deleted.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="px-3 py-1.5 text-sm border rounded-md border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => setArchiveConfirmId(null)}>Cancel</button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-md bg-red-600 text-white hover:bg-red-700"
                onClick={async () => {
                  try {
                    await blogCandidatesService.update(archiveConfirmId, { status: 'archived' });
                    setArchiveConfirmId(null);
                    await loadArticles();
                  } catch (e: any) {
                    onArticlesError(e?.message || 'Failed to delete article');
                    setArchiveConfirmId(null);
                  }
                }}
              >Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
